import { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { api } from "@/lib/api";
import { HTTP_UNAUTHORIZED } from "@/lib/constants";
import { Activity, MousePointerClick, Send, TrendingUp, ArrowUp, ArrowDown } from "lucide-react";
import EstimatorHeatmap from "@/pages/admin/EstimatorHeatmap";

const WINDOWS = [
  { key: 7, label: "7d" },
  { key: 30, label: "30d" },
  { key: 90, label: "90d" },
  { key: null, label: "All" },
];

const fmtPct = (n) => `${(n ?? 0).toFixed(1)}%`;
const fmtUsd = (n) =>
  (n ?? 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const fmtShortDate = (iso) => {
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  return `${Number(m)}/${Number(d)}`;
};

function DeltaChip({ value }) {
  if (value === undefined || value === null) return null;
  const positive = value > 0;
  const zero = value === 0;
  const Icon = positive ? ArrowUp : ArrowDown;
  const tone = zero
    ? "bg-[#F4EEE8] text-[#8C7A70]"
    : positive
    ? "bg-emerald-50 text-emerald-700"
    : "bg-rose-50 text-rose-700";
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${tone}`}
      data-testid="delta-chip"
    >
      {!zero && <Icon size={10} strokeWidth={2.5} />}
      {Math.abs(value).toFixed(1)}%
    </span>
  );
}

function StatCard({ icon: Icon, label, value, hint, delta, testId }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E8DFD8] p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[#8C7A70]">
          <Icon size={14} strokeWidth={1.5} />
          <span className="text-[10px] uppercase tracking-[0.14em] font-medium">{label}</span>
        </div>
        <DeltaChip value={delta} />
      </div>
      <p className="font-serif-lux text-3xl mt-2 text-[#2A1C14]">{value}</p>
      {hint ? <p className="text-[11px] text-[#8C7A70] mt-1">{hint}</p> : null}
    </div>
  );
}

function WindowTabs({ value, onChange }) {
  return (
    <div
      className="inline-flex items-center rounded-full bg-[#F4EEE8] border border-[#E8DFD8] p-1 gap-1"
      data-testid="funnel-window-tabs"
    >
      {WINDOWS.map((w) => {
        const active = value === w.key;
        return (
          <button
            key={w.label}
            type="button"
            onClick={() => onChange(w.key)}
            data-testid={`funnel-window-${w.label}`}
            className={
              "text-xs font-medium tracking-wide px-3.5 py-1.5 rounded-full transition-colors " +
              (active
                ? "bg-[#2A1C14] text-[#FCFAF8] shadow-sm"
                : "text-[#5A3B31] hover:text-[#2A1C14]")
            }
          >
            {w.label}
          </button>
        );
      })}
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-[#E8DFD8] bg-white/95 backdrop-blur px-4 py-3 shadow-lg text-xs">
      <p className="text-[10px] uppercase tracking-[0.14em] text-[#8C7A70]">{label}</p>
      <ul className="mt-1.5 space-y-1">
        {payload.map((p) => (
          <li key={p.dataKey} className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
            <span className="text-[#5A3B31]">{p.name}</span>
            <span className="ml-auto font-semibold text-[#2A1C14]">{p.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FunnelChart({ series }) {
  const data = useMemo(
    () => (series || []).map((r) => ({ ...r, date: fmtShortDate(r.date) })),
    [series]
  );
  const hasData = data.some((d) => d.interact || d.apply_click || d.applications);
  if (!hasData) {
    return (
      <div className="rounded-2xl border border-dashed border-[#E8DFD8] bg-white/50 p-10 text-center" data-testid="funnel-chart-empty">
        <p className="font-serif-lux text-xl text-[#5A3B31]">No activity in this window yet.</p>
        <p className="text-xs text-[#8C7A70] mt-2">
          Once agents start engaging with the estimator, the daily trend will appear here.
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl bg-white border border-[#E8DFD8] p-5" data-testid="funnel-chart">
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 12, left: -8, bottom: 0 }}>
            <CartesianGrid stroke="#F4EEE8" strokeDasharray="3 6" vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={{ stroke: "#E8DFD8" }}
              tick={{ fill: "#8C7A70", fontSize: 11 }}
              interval="preserveStartEnd"
              minTickGap={24}
            />
            <YAxis
              allowDecimals={false}
              tickLine={false}
              axisLine={false}
              tick={{ fill: "#8C7A70", fontSize: 11 }}
              width={32}
            />
            <Tooltip content={<ChartTooltip />} />
            <Legend
              iconType="circle"
              wrapperStyle={{ fontSize: 11, color: "#5A3B31", paddingTop: 6 }}
            />
            <Line
              type="monotone"
              dataKey="interact"
              name="Interactions"
              stroke="#CBA4A4"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="apply_click"
              name="Apply Clicks"
              stroke="#5A3B31"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="applications"
              name="Applications"
              stroke="#2A1C14"
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function EstimatorAnalytics({ onUnauthorized }) {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = days ? { days } : {};
        const { data } = await api.get("/admin/estimator/stats", { params });
        if (!cancelled) {
          setStats(data);
          setError(false);
        }
      } catch (err) {
        if (err?.response?.status === HTTP_UNAUTHORIZED) {
          onUnauthorized?.();
          return;
        }
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days, onUnauthorized]);

  if (error || (!loading && !stats)) return null;

  const summary = stats?.summary || {};
  const previous = stats?.previous || null;
  const series = stats?.series || [];
  const heatmap = stats?.heatmap || null;
  const windowLabel = days ? `last ${days} days` : "all time";

  return (
    <section className="mb-10" data-testid="estimator-analytics">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5">
        <div>
          <span className="overline flex items-center gap-2">
            <TrendingUp size={12} strokeWidth={1.5} /> Estimator Funnel
          </span>
          <h2 className="font-serif-lux text-3xl mt-2 text-[#2A1C14]">
            Widget performance <span className="text-[#8C7A70] text-lg">· {windowLabel}</span>
          </h2>
        </div>
        <WindowTabs value={days} onChange={setDays} />
      </div>

      {loading ? (
        <div className="lux-card p-6" data-testid="estimator-analytics-loading">
          <p className="text-sm text-[#8C7A70]">Loading...</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              testId="stat-interact"
              icon={Activity}
              label="Interactions"
              value={(summary.interact_sessions ?? 0).toLocaleString()}
              hint="unique sessions that moved the slider"
              delta={previous?.delta_interact_pct}
            />
            <StatCard
              testId="stat-apply-click"
              icon={MousePointerClick}
              label="Apply Clicks"
              value={(summary.apply_click_sessions ?? 0).toLocaleString()}
              hint={`${(summary.apply_click_events ?? 0).toLocaleString()} total clicks`}
              delta={previous?.delta_apply_click_pct}
            />
            <StatCard
              testId="stat-applications"
              icon={Send}
              label="Applications from Estimator"
              value={(summary.estimator_applications ?? 0).toLocaleString()}
              hint={`of ${(summary.total_applications ?? 0).toLocaleString()} total submitted`}
              delta={previous?.delta_applications_pct}
            />
            <StatCard
              testId="stat-avg-advance"
              icon={TrendingUp}
              label="Avg Advance Requested"
              value={fmtUsd(summary.avg_advance)}
              hint={`avg net: ${fmtUsd(summary.avg_net_commission)}`}
            />
          </div>

          <div className="mt-4 rounded-2xl bg-[#F4EEE8] border border-[#E8DFD8] p-5" data-testid="funnel-bar">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-6 flex-wrap">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-[#8C7A70]">Interact → Apply Click</p>
                  <p className="font-serif-lux text-2xl mt-1 text-[#2A1C14]" data-testid="stat-conv-interact-apply">
                    {fmtPct(summary.interact_to_apply_click_pct)}
                  </p>
                </div>
                <div className="hidden sm:block w-px h-10 bg-[#E8DFD8]" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-[#8C7A70]">Apply Click → Application</p>
                  <p className="font-serif-lux text-2xl mt-1 text-[#2A1C14]" data-testid="stat-conv-apply-app">
                    {fmtPct(summary.apply_click_to_application_pct)}
                  </p>
                </div>
              </div>
              <p className="text-[11px] text-[#8C7A70] max-w-xs">
                High interact-to-click rates mean the estimator is convincing. High click-to-application
                rates mean the pre-filled form is closing the deal.
              </p>
            </div>
          </div>

          <div className="mt-4">
            <FunnelChart series={series} />
          </div>
          <EstimatorHeatmap heatmap={heatmap} />
        </>
      )}
    </section>
  );
}
